import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import { 
  FileCheck, Shield, Clock, Heart, Users, AlertTriangle,
  CheckCircle, ArrowLeft, HandHeart, Ban, MessageCircle
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Navbar } from '@/components/layout/Navbar';
import { Footer } from '@/components/layout/Footer';

const TravelerCharterPage = () => {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const [accepted, setAccepted] = useState(false);

  const commitments = [
    {
      icon: Clock,
      title: i18n.language === 'fr' ? 'Ponctualité' : 'Punctuality',
      description: i18n.language === 'fr' 
        ? 'Je m\'engage à être à l\'heure au point de rendez-vous. En cas d\'empêchement, je préviens le guide le plus tôt possible.'
        : 'I commit to being on time at the meeting point. If prevented, I notify the guide as soon as possible.',
      color: 'text-blue-600',
      bgColor: 'bg-blue-50'
    },
    {
      icon: Heart,
      title: i18n.language === 'fr' ? 'Respect' : 'Respect',
      description: i18n.language === 'fr' 
        ? 'Je respecte le guide, les autres participants, les lieux visités et les cultures locales. Je suis courtois(e) en toutes circonstances.'
        : 'I respect the guide, other participants, visited places, and local cultures. I am courteous at all times.',
      color: 'text-red-500',
      bgColor: 'bg-red-50'
    },
    {
      icon: Shield,
      title: i18n.language === 'fr' ? 'Sécurité' : 'Safety',
      description: i18n.language === 'fr' 
        ? 'Je suis les consignes de sécurité données par le guide. Je signale tout problème de santé ou limitation physique avant l\'expérience.'
        : 'I follow safety instructions given by the guide. I report any health issues or physical limitations before the experience.',
      color: 'text-green-600',
      bgColor: 'bg-green-50'
    },
    {
      icon: MessageCircle,
      title: i18n.language === 'fr' ? 'Communication' : 'Communication',
      description: i18n.language === 'fr' 
        ? 'Je communique uniquement via la messagerie XPerience avant la réservation confirmée. Je ne demande pas les coordonnées personnelles du guide.'
        : 'I communicate only through XPerience messaging before confirmed booking. I do not ask for the guide\'s personal contact details.',
      color: 'text-purple-600',
      bgColor: 'bg-purple-50'
    },
    {
      icon: HandHeart,
      title: i18n.language === 'fr' ? 'Bienveillance' : 'Kindness',
      description: i18n.language === 'fr' 
        ? 'Je fais preuve de bienveillance et d\'ouverture d\'esprit. Je suis là pour vivre une expérience authentique et partager un moment humain.'
        : 'I show kindness and open-mindedness. I am here to live an authentic experience and share a human moment.',
      color: 'text-amber-600',
      bgColor: 'bg-amber-50'
    }
  ];

  const rules = [
    {
      icon: Ban,
      title: i18n.language === 'fr' ? 'Comportements interdits' : 'Prohibited behaviors',
      items: i18n.language === 'fr' ? [
        'Harcèlement, discrimination ou propos inappropriés',
        'État d\'ébriété ou sous l\'influence de substances',
        'Non-respect des consignes de sécurité',
        'Tentative de paiement hors plateforme',
        'Fausses réclamations ou abus du système de remboursement'
      ] : [
        'Harassment, discrimination, or inappropriate comments',
        'Being drunk or under the influence of substances',
        'Disregarding safety instructions',
        'Attempting payment outside the platform',
        'False claims or abuse of the refund system'
      ]
    }
  ];

  const guideRights = [
    {
      title: i18n.language === 'fr' ? 'Droit d\'interruption' : 'Right to interrupt',
      description: i18n.language === 'fr'
        ? 'Le guide peut interrompre l\'expérience à tout moment en cas de comportement inapproprié, sans remboursement pour le voyageur.'
        : 'The guide can interrupt the experience at any time in case of inappropriate behavior, without refund for the traveler.'
    },
    {
      title: i18n.language === 'fr' ? 'Droit de refus' : 'Right to refuse',
      description: i18n.language === 'fr'
        ? 'Le guide peut refuser un voyageur ayant un historique de mauvais comportements ou de mauvaises évaluations.'
        : 'The guide can refuse a traveler with a history of bad behavior or poor ratings.'
    },
    {
      title: i18n.language === 'fr' ? 'Droit de signalement' : 'Right to report',
      description: i18n.language === 'fr'
        ? 'Le guide peut signaler tout comportement inapproprié. XPerience prendra les mesures nécessaires.'
        : 'The guide can report any inappropriate behavior. XPerience will take necessary measures.'
    }
  ];

  const penalties = [
    { offense: i18n.language === 'fr' ? 'No-show sans prévenir' : 'No-show without notice', penalty: i18n.language === 'fr' ? 'Pas de remboursement + avertissement' : 'No refund + warning' },
    { offense: i18n.language === 'fr' ? '2ème no-show' : '2nd no-show', penalty: i18n.language === 'fr' ? 'Suspension temporaire du compte' : 'Temporary account suspension' },
    { offense: i18n.language === 'fr' ? 'Comportement inapproprié' : 'Inappropriate behavior', penalty: i18n.language === 'fr' ? 'Avertissement puis bannissement' : 'Warning then ban' },
    { offense: i18n.language === 'fr' ? 'Fausse réclamation' : 'False claim', penalty: i18n.language === 'fr' ? 'Enquête + possible bannissement' : 'Investigation + possible ban' },
    { offense: i18n.language === 'fr' ? 'Harcèlement' : 'Harassment', penalty: i18n.language === 'fr' ? 'Bannissement immédiat' : 'Immediate ban' }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-orange-50 to-white">
      <Navbar />
      
      <main className="container mx-auto px-4 py-12 max-w-4xl">
        {/* Back button */}
        <Link to="/" className="inline-flex items-center text-gray-600 hover:text-primary mb-8">
          <ArrowLeft className="w-4 h-4 mr-2" />
          {i18n.language === 'fr' ? 'Retour' : 'Back'}
        </Link>

        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-primary/10 mb-6">
            <FileCheck className="w-10 h-10 text-primary" />
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            {i18n.language === 'fr' ? 'Charte des Voyageurs' : 'Traveler Charter'}
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            {i18n.language === 'fr' 
              ? 'Pour garantir une expérience positive pour tous, chaque voyageur s\'engage à respecter cette charte.'
              : 'To ensure a positive experience for everyone, each traveler commits to respecting this charter.'}
          </p>
        </motion.div>

        {/* Introduction */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-2xl shadow-lg p-8 mb-8"
        >
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
              <Users className="w-6 h-6 text-primary" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900 mb-3">
                {i18n.language === 'fr' ? 'Bienvenue dans la communauté XPerience !' : 'Welcome to the XPerience community!'}
              </h2>
              <p className="text-gray-600 leading-relaxed">
                {i18n.language === 'fr'
                  ? 'Nos guides partagent leur passion, leur savoir et leur temps pour vous offrir des expériences authentiques. En retour, nous attendons de vous un comportement respectueux et bienveillant. Cette charte définit les engagements mutuels pour que chaque rencontre soit un moment agréable.'
                  : 'Our guides share their passion, knowledge, and time to offer you authentic experiences. In return, we expect respectful and kind behavior from you. This charter defines mutual commitments so that each encounter is a pleasant moment.'}
              </p>
            </div>
          </div>
        </motion.div>

        {/* Membership & Access Section - NEW */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 }}
          className="bg-purple-50 border-2 border-purple-200 rounded-2xl p-8 mb-8"
        >
          <h2 className="text-2xl font-bold text-purple-700 mb-6 flex items-center gap-3">
            <span className="text-2xl">🎫</span>
            {i18n.language === 'fr' ? 'Accès aux expériences' : 'Access to experiences'}
          </h2>
          <div className="space-y-4">
            <div className="bg-white rounded-lg p-4">
              <h3 className="font-semibold text-purple-700 mb-2">
                {i18n.language === 'fr' ? '💰 Expériences payantes' : '💰 Paid experiences'}
              </h3>
              <p className="text-gray-600 text-sm">
                {i18n.language === 'fr'
                  ? 'Ouvertes à tous les voyageurs, membre ou non. Tu paies le prix affiché par le guide.'
                  : 'Open to all travelers, member or not. You pay the price set by the guide.'}
              </p>
            </div>
            <div className="bg-white rounded-lg p-4">
              <h3 className="font-semibold text-purple-700 mb-2">
                {i18n.language === 'fr' ? '🤝 Rencontres gratuites (Meetups)' : '🤝 Free meetups'}
              </h3>
              <p className="text-gray-600 text-sm">
                {i18n.language === 'fr'
                  ? 'Réservées aux membres XPerience. Abonnement annuel de 49€ pour un accès illimité à toutes les expériences gratuites.'
                  : 'Reserved for XPerience members. €49 annual subscription for unlimited access to all free experiences.'}
              </p>
              <Link to="/membership" className="text-sm text-purple-600 hover:underline font-medium">
                {i18n.language === 'fr' ? '→ Devenir membre' : '→ Become a member'}
              </Link>
            </div>
            <div className="bg-white rounded-lg p-4">
              <h3 className="font-semibold text-purple-700 mb-2">
                {i18n.language === 'fr' ? '🎪 Événements locaux' : '🎪 Local events'}
              </h3>
              <p className="text-gray-600 text-sm">
                {i18n.language === 'fr'
                  ? 'Gratuits et ouverts à tous ! Festivals, marchés, fêtes locales...'
                  : 'Free and open to everyone! Festivals, markets, local celebrations...'}
              </p>
            </div>
          </div>
        </motion.div>

        {/* Payment & Escrow - NEW */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.18 }}
          className="bg-blue-50 border-2 border-blue-200 rounded-2xl p-8 mb-8"
        >
          <h2 className="text-2xl font-bold text-blue-700 mb-6 flex items-center gap-3">
            <span className="text-2xl">💳</span>
            {i18n.language === 'fr' ? 'Paiement sécurisé' : 'Secure payment'}
          </h2>
          <div className="space-y-3 text-blue-800">
            <p className="flex items-start gap-2">
              <span className="text-blue-500 mt-1">•</span>
              <span>
                {i18n.language === 'fr'
                  ? 'Ton paiement est retenu par XPerience jusqu\'à la réalisation de l\'expérience.'
                  : 'Your payment is held by XPerience until the experience is completed.'}
              </span>
            </p>
            <p className="flex items-start gap-2">
              <span className="text-blue-500 mt-1">•</span>
              <span>
                {i18n.language === 'fr'
                  ? 'Après l\'expérience, tu as 48h pour confirmer ou ouvrir un litige.'
                  : 'After the experience, you have 48h to confirm or open a dispute.'}
              </span>
            </p>
            <p className="flex items-start gap-2">
              <span className="text-blue-500 mt-1">•</span>
              <span>
                {i18n.language === 'fr'
                  ? 'Le guide reçoit son paiement 48h après ta confirmation.'
                  : 'The guide receives payment 48h after your confirmation.'}
              </span>
            </p>
          </div>
        </motion.div>

        {/* Cancellation Policy - NEW */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.19 }}
          className="bg-green-50 border-2 border-green-200 rounded-2xl p-8 mb-8"
        >
          <h2 className="text-2xl font-bold text-green-700 mb-6 flex items-center gap-3">
            <span className="text-2xl">📅</span>
            {i18n.language === 'fr' ? 'Politique d\'annulation' : 'Cancellation policy'}
          </h2>
          <div className="space-y-2 text-green-800">
            <div className="flex justify-between py-2 border-b border-green-200">
              <span>{i18n.language === 'fr' ? 'Plus de 7 jours avant' : 'More than 7 days before'}</span>
              <span className="font-semibold">{i18n.language === 'fr' ? 'Remboursement 100%' : '100% refund'}</span>
            </div>
            <div className="flex justify-between py-2 border-b border-green-200">
              <span>{i18n.language === 'fr' ? '3 à 7 jours avant' : '3 to 7 days before'}</span>
              <span className="font-semibold">{i18n.language === 'fr' ? 'Remboursement 50%' : '50% refund'}</span>
            </div>
            <div className="flex justify-between py-2 border-b border-green-200">
              <span>{i18n.language === 'fr' ? 'Moins de 3 jours' : 'Less than 3 days'}</span>
              <span className="font-semibold">{i18n.language === 'fr' ? 'Pas de remboursement' : 'No refund'}</span>
            </div>
            <div className="flex justify-between py-2">
              <span>{i18n.language === 'fr' ? 'Annulation par le guide' : 'Cancellation by guide'}</span>
              <span className="font-semibold text-green-600">{i18n.language === 'fr' ? 'Remboursement 100%' : '100% refund'}</span>
            </div>
          </div>
        </motion.div>

        {/* Commitments */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white rounded-2xl shadow-lg p-8 mb-8"
        >
          <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-3">
            <CheckCircle className="w-7 h-7 text-primary" />
            {i18n.language === 'fr' ? 'Mes engagements' : 'My commitments'}
          </h2>
          <div className="space-y-4">
            {commitments.map((commitment, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.3 + index * 0.1 }}
                className={`flex items-start gap-4 p-4 rounded-xl ${commitment.bgColor}`}
              >
                <div className={`w-10 h-10 rounded-full bg-white flex items-center justify-center flex-shrink-0`}>
                  <commitment.icon className={`w-5 h-5 ${commitment.color}`} />
                </div>
                <div>
                  <h3 className={`font-semibold ${commitment.color}`}>{commitment.title}</h3>
                  <p className="text-gray-600 text-sm mt-1">{commitment.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Prohibited behaviors */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="bg-red-50 border-2 border-red-200 rounded-2xl p-8 mb-8"
        >
          <h2 className="text-2xl font-bold text-red-700 mb-6 flex items-center gap-3">
            <Ban className="w-7 h-7" />
            {i18n.language === 'fr' ? 'Comportements interdits' : 'Prohibited behaviors'}
          </h2>
          <ul className="space-y-3">
            {rules[0].items.map((item, index) => (
              <li key={index} className="flex items-center gap-3 text-red-800">
                <span className="w-2 h-2 rounded-full bg-red-500"></span>
                {item}
              </li>
            ))}
          </ul>
        </motion.div>

        {/* Guide Rights */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="bg-blue-50 border-2 border-blue-200 rounded-2xl p-8 mb-8"
        >
          <h2 className="text-2xl font-bold text-blue-700 mb-6 flex items-center gap-3">
            <Shield className="w-7 h-7" />
            {i18n.language === 'fr' ? 'Droits des guides' : 'Guide rights'}
          </h2>
          <p className="text-blue-800 mb-4">
            {i18n.language === 'fr'
              ? 'Pour protéger nos guides, XPerience leur accorde les droits suivants :'
              : 'To protect our guides, XPerience grants them the following rights:'}
          </p>
          <div className="space-y-4">
            {guideRights.map((right, index) => (
              <div key={index} className="bg-white rounded-lg p-4">
                <h3 className="font-semibold text-blue-700">{right.title}</h3>
                <p className="text-gray-600 text-sm mt-1">{right.description}</p>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Reporting System - NEW */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.55 }}
          className="bg-orange-50 border-2 border-orange-200 rounded-2xl p-8 mb-8"
        >
          <h2 className="text-2xl font-bold text-orange-700 mb-6 flex items-center gap-3">
            <MessageCircle className="w-7 h-7" />
            {i18n.language === 'fr' ? 'Système de signalement' : 'Reporting system'}
          </h2>
          <p className="text-orange-800 mb-4">
            {i18n.language === 'fr'
              ? 'En cas de problème avec un guide, tu peux le signaler directement depuis la messagerie :'
              : 'If you have an issue with a guide, you can report them directly from the messaging:'}
          </p>
          <ul className="text-orange-800 space-y-2">
            <li className="flex items-start gap-2">
              <span className="text-orange-500 mt-1">•</span>
              <span>{i18n.language === 'fr' ? 'Comportement inapproprié' : 'Inappropriate behavior'}</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-orange-500 mt-1">•</span>
              <span>{i18n.language === 'fr' ? 'Harcèlement' : 'Harassment'}</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-orange-500 mt-1">•</span>
              <span>{i18n.language === 'fr' ? 'Arnaque ou fraude' : 'Scam or fraud'}</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-orange-500 mt-1">•</span>
              <span>{i18n.language === 'fr' ? 'Non-présentation du guide' : 'Guide no-show'}</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-orange-500 mt-1">•</span>
              <span>{i18n.language === 'fr' ? 'Problème de sécurité' : 'Safety concern'}</span>
            </li>
          </ul>
          <p className="text-orange-700 mt-4 text-sm">
            {i18n.language === 'fr'
              ? 'Notre équipe examine chaque signalement et prend les mesures nécessaires.'
              : 'Our team reviews each report and takes necessary action.'}
          </p>
        </motion.div>

        {/* Penalties */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="bg-amber-50 border-2 border-amber-200 rounded-2xl p-8 mb-8"
        >
          <h2 className="text-2xl font-bold text-amber-700 mb-6 flex items-center gap-3">
            <AlertTriangle className="w-7 h-7" />
            {i18n.language === 'fr' ? 'Sanctions' : 'Penalties'}
          </h2>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b-2 border-amber-300">
                  <th className="text-left py-2 text-amber-800">{i18n.language === 'fr' ? 'Infraction' : 'Offense'}</th>
                  <th className="text-left py-2 text-amber-800">{i18n.language === 'fr' ? 'Sanction' : 'Penalty'}</th>
                </tr>
              </thead>
              <tbody>
                {penalties.map((item, index) => (
                  <tr key={index} className="border-b border-amber-200">
                    <td className="py-3 text-gray-700">{item.offense}</td>
                    <td className="py-3 text-amber-700 font-medium">{item.penalty}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </motion.div>

        {/* Acceptance */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7 }}
          className="bg-white rounded-2xl shadow-lg p-8"
        >
          <div className="flex items-start gap-3 mb-6">
            <Checkbox
              id="accept-charter"
              checked={accepted}
              onCheckedChange={setAccepted}
              className="mt-1"
            />
            <label htmlFor="accept-charter" className="cursor-pointer">
              <span className="font-semibold text-gray-900">
                {i18n.language === 'fr' 
                  ? 'J\'ai lu et j\'accepte la Charte des Voyageurs XPerience'
                  : 'I have read and accept the XPerience Traveler Charter'}
              </span>
              <p className="text-sm text-gray-500 mt-1">
                {i18n.language === 'fr'
                  ? 'En acceptant cette charte, je m\'engage à respecter ces règles lors de chaque expérience.'
                  : 'By accepting this charter, I commit to respecting these rules during each experience.'}
              </p>
            </label>
          </div>

          <Button
            onClick={() => navigate('/')}
            disabled={!accepted}
            className="w-full py-6 text-lg"
          >
            {i18n.language === 'fr' ? 'J\'accepte et je continue' : 'I accept and continue'}
          </Button>
        </motion.div>
      </main>

      <Footer />
    </div>
  );
};

export default TravelerCharterPage;
